import constants from '../constants';
import { getContainerData } from '../container/container-utils';

const contextMap: { [key: string]: number } = {};
const transientContextMap: { [key: string]: any } = new Map();

/**
 * this function helps to get Parent context name from context
 * @ignore
 * @param context -- base context
 * @returns -- parent context name
 */
export const getParentContextName = (context: string) => {
  let parentContext = constants.EMPTY_STRING;
  if (context && context.includes(constants.FORWARD_SLASH_STRING)) {
    parentContext = context.substring(0, context.lastIndexOf('/'));
  } else {
    parentContext = context;
  }
  return parentContext;
};

/**
 * this function helps to build context name
 * @ignore
 * @param target -- container target name
 * @param baseContext -- base context
 * @param returnExistingContext if true, return the existing context
 * @returns -- new context name
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const buildContextName = (target: string, baseContext: string, returnExistingContext = false) => {
  let context = constants.EMPTY_STRING;
  if (baseContext && target) {
    let newTargetContainerName = `${baseContext}${constants.FORWARD_SLASH_STRING}${target}`;
    const isTargetContainerPresent = getContainerData(newTargetContainerName);

    if (!isTargetContainerPresent && (baseContext === constants.APP.APP || baseContext === constants.APP.ROOT)) {
      newTargetContainerName = `${baseContext}${constants.FORWARD_SLASH_STRING}${constants.PRIMARY}`;

      if (contextMap[newTargetContainerName]) {
        contextMap[newTargetContainerName] += 1;
      } else {
        contextMap[newTargetContainerName] = 1;
      }

      context = `${newTargetContainerName}${constants.UNDERSCORE_STRING}${contextMap[newTargetContainerName]}`;
    } else if (!isTargetContainerPresent) {
      context = buildContextName(target, getParentContextName(baseContext));
    } else {
      if (returnExistingContext && contextMap[newTargetContainerName]) {
        context = `${newTargetContainerName}${constants.UNDERSCORE_STRING}${contextMap[newTargetContainerName]}`;
        return context;
      }
      if (contextMap[newTargetContainerName]) {
        contextMap[newTargetContainerName] += 1;
      } else {
        contextMap[newTargetContainerName] = 1;
      }
      context = `${newTargetContainerName}${constants.UNDERSCORE_STRING}${contextMap[newTargetContainerName]}`;
    }
  }
  return context;
};

/**
 * this function helps to build transient context name
 * @private
 * @param id unique id to be added as transient item
 * @param baseContext base context to start build from
 * @returns newly built contextName
 */
export const buildTransientContextName = (id: string, baseContext: string) => {
  if (id && baseContext) {
    const itemCount = (transientContextMap[baseContext]?.[id] ?? 0) + 1;
    if (!transientContextMap[baseContext]) {
      transientContextMap[baseContext] = {};
    }
    transientContextMap[baseContext][id] = itemCount;
    return `${baseContext}${constants.FORWARD_SLASH_STRING}${id}_${itemCount}`;
  }
  return '';
};

/**
 * this function returns the transient context for the target container
 * @private
 * @param id transient item unique id
 * @param baseContext base context to start search from
 * @returns context if exists else empty string
 */
export const getTransientContext = (id: string | undefined, baseContext: string) => {
  if (id && baseContext) {
    const itemCount = transientContextMap[baseContext]?.[id];
    if (itemCount) {
      return `${baseContext}${constants.FORWARD_SLASH_STRING}${id}_${itemCount}`;
    }
    return buildTransientContextName(id, baseContext);
  }
  return '';
};

/**
 * this function helps to get Target name from context
 * @ignore
 * @param context -- base context
 * @returns -- target name
 */
export const getTargetFromContext = (context: string) => {
  let targetContainer = constants.EMPTY_STRING;
  if (context) {
    targetContainer = context.substring(context.lastIndexOf('/') + 1, context.lastIndexOf('_')).toLowerCase();
  }
  return targetContainer;
};

/**
 * Creates a context name for the modal in the format `app/primary_3/secondary_1'. Here `app/primary_3` is the parent context.
 * If the modal is opened from peek panel (tertiary), the context name will be in the format: `app/tertiary_1/secondary_1'
 * For skip new use case the format will be `app/secondary_1'
 * @param parentContext - Parent context from where modal is opened
 * @param context - new context for the secondary (modal)
 * @ignore
 */
export const buildModalContext = (parentContext: string, context: string) => {
  if (parentContext === null) return context;
  const [, secondaryName] = context.split(constants.FORWARD_SLASH_STRING);
  return parentContext + constants.FORWARD_SLASH_STRING + secondaryName;
};

/**
 * Return parent context for the modal, the context from where the modal is opened. For E.g.: `app/primary_1` and `app/tertiary_1`.
 * For skip new use case we don't have any parent context
 * @param context - modal context
 * @ignore
 */
export const getParentContext = (context: string) => {
  const firstSlashIndex = context.indexOf(constants.FORWARD_SLASH_STRING);
  const lastSlashIndex = context.lastIndexOf(constants.FORWARD_SLASH_STRING);

  // Skip new - modal use case.
  if (firstSlashIndex === lastSlashIndex) {
    return context;
  }

  return context.substring(0, lastSlashIndex);
};

/**
 * Get container from context name, this method is used in modal use cases. E.g: `app/primary_3/secondary_1' will return `app/secondary_1'
 * @param context - modal context
 * @ignore
 */
export const getContainerIdFromContext = (context: string) => {
  const tokens = context.split(constants.FORWARD_SLASH_STRING);
  // Skip new use case - context will be like `app/secondary_1`
  if (tokens.length === 2) {
    return context;
  }
  return tokens[0] + constants.FORWARD_SLASH_STRING + tokens[2];
};
